import { router } from "@inertiajs/react"
import { toast } from "sonner"

import { t } from "@/lib/i18n"

export type InlineUpdateResult = {
  message?: string
  description?: string
}

type FlashMessage = string | { header?: string; description?: string } | undefined

function flashText(message: FlashMessage) {
  if (typeof message === "string") return message
  return [message?.header, message?.description].filter(Boolean).join(" ")
}

type InlineUpdateData = Parameters<typeof router.patch>[1]

type InlineUpdateOptions = {
  preserveState?: boolean
}

// Wraps an Inertia POST/PATCH/PUT/DELETE into the promise contract expected by
// the inline-update components: resolves with the server flash notice so the
// success toast reuses the localized message, rejects with the joined
// validation errors so the failure toast can show the detail.
function submitInline(
  method: "post" | "patch" | "put" | "delete",
  url: string,
  data: InlineUpdateData,
  options: InlineUpdateOptions = {}
): Promise<InlineUpdateResult> {
  return new Promise((resolve, reject) => {
    const visitOptions = {
      preserveScroll: true,
      ...options,
      // Structural, so the shared options object works for every verb without
      // pulling @inertiajs/core in as a direct dependency just for a type.
      onSuccess: (page: { props: Record<string, unknown> }) => {
        const flash = page.props.flash as { notice?: FlashMessage; alert?: FlashMessage } | undefined

        // Controllers report some failures as a redirect with a flash alert
        // instead of a 422 with validation errors — treat those as failures.
        if (flash?.alert) {
          reject(new Error(flashText(flash.alert)))
          return
        }

        const notice = flash?.notice

        if (typeof notice === "string") {
          resolve({ message: notice })
        } else {
          resolve({ message: notice?.header, description: notice?.description })
        }
      },
      onError: (errors: Record<string, string | string[]>) => {
        reject(new Error(Object.values(errors).flat().join(" ")))
      },
    }

    // `router.delete` takes options as its SECOND argument — it has no data
    // parameter. Handing it the (url, data, options) shape the other verbs use
    // makes Inertia read the payload as the options, drop onSuccess/onError,
    // and leave this promise unsettled forever: the caller's spinner never
    // stops and no toast ever fires.
    if (method === "delete") {
      router.delete(url, visitOptions)
      return
    }

    router[method](url, data, visitOptions)
  })
}

export function postInline(
  url: string,
  data: InlineUpdateData = {},
  options: InlineUpdateOptions = {}
): Promise<InlineUpdateResult> {
  return submitInline("post", url, data, options)
}

export function patchInline(
  url: string,
  data: InlineUpdateData = {},
  options: InlineUpdateOptions = {}
): Promise<InlineUpdateResult> {
  return submitInline("patch", url, data, options)
}

export function putInline(
  url: string,
  data: InlineUpdateData = {},
  options: InlineUpdateOptions = {}
): Promise<InlineUpdateResult> {
  return submitInline("put", url, data, options)
}

export function deleteInline(
  url: string,
  data: InlineUpdateData = {},
  options: InlineUpdateOptions = {}
): Promise<InlineUpdateResult> {
  return submitInline("delete", url, data, options)
}

export function toastInlineUpdateSuccess(
  result: InlineUpdateResult | void,
  successMessage?: string
) {
  toast.success(
    result?.message || successMessage || t("ui.react.components.ui.inline_update.updated"),
    { description: result?.description }
  )
}

export function toastInlineUpdateError(error: unknown, errorMessage?: string) {
  toast.error(
    errorMessage || t("ui.react.components.ui.inline_update.update_failed"),
    {
      description:
        error instanceof Error && error.message ? error.message : undefined,
    }
  )
}
